import React, {
  KeyboardEventHandler,
  RefObject,
  useEffect,
  useState,
} from "react";
import { Textarea } from "@mui/joy";
import { isMobile } from "react-device-detect";
import { theme } from "styles/theme";

interface TextareaRefundProps {
  placeholder?: string;
  initialValue?: string;
  handleChange: (value: string) => void;
  isFocused: boolean;
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
  inputRef?: RefObject<HTMLTextAreaElement>;
  onValidityChange?: (isValid: boolean) => void; // 이메일 유효성 변경 콜백 (선택)
}

function isValidEmail(email: string) {
  // 매우 기본적인 이메일 유효성 검사 (공백/@/도메인 포함)
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function EmailSubmit({
  placeholder,
  initialValue,
  handleChange,
  isFocused,
  setIsFocused,
  inputRef,
  onValidityChange,
}: TextareaRefundProps) {
  const [value, setValue] = useState(initialValue);

  // 포커스 해제 시 외부에서 전달된 값으로 동기화
  useEffect(() => {
    if (!isFocused) {
      setValue(initialValue);
    }
  }, [initialValue, isFocused]);

  // 값 변경 시 유효성 콜백 알림
  useEffect(() => {
    if (onValidityChange) {
      onValidityChange(isValidEmail(value || ""));
    }
  }, [value, onValidityChange]);

  return (
    <Textarea
      placeholder={placeholder}
      value={value || ""}
      maxRows={1}
      onChange={(e) => {
        const newValue = e.target.value;
        setValue(newValue);
        handleChange(newValue);
        if (onValidityChange) {
          onValidityChange(isValidEmail(newValue));
        }
      }}
      readOnly={false}
      minRows={1}
      onFocus={() => setIsFocused(true)}
      onBlur={() => {
        setIsFocused(false);
        if (onValidityChange) {
          onValidityChange(isValidEmail((value || "").trim()));
        }
      }}
      slotProps={{
        textarea: {
          ref: inputRef,
          inputMode: "email",
          maxLength: 500,
        },
      }}
      sx={{
        width: "100%",
        height: "auto",
        boxShadow: "none",
        backgroundColor: "transparent",
        padding: "12.5px 16px",
        border: "none",
        minWidth: 0,
        minHeight: "24px",
        "--joy-focus-thickness": "0px",
        "--variant-borderWidth": "0px",
        "--Textarea-gap": "0px",
        "& textarea": {
          textAlign: "left",
          padding: 0,
          margin: 0,
          fontWeight: 500,
          color: theme.vars.palette.Content.Strong,
          whiteSpace: "pre-wrap",
          overflowX: "hidden",
          resize: "none",
          fontSize: "16px",
          lineHeight: "132%",
        },
        "& textarea::placeholder": {
          textAlign: "left",
          whiteSpace: "pre-wrap",
          fontWeight: 500,
          color: theme.vars.palette.Content.SuperWeak,
          fontSize: "16px",
          lineHeight: "132%",
        },
      }}
    />
  );
}